import type { UnionAddress } from "@anaconda/core/blockchain/address"
import { isExternalListing } from "@anaconda/core/nft/listing-source"
import type { NftMiniature } from "@anaconda/core/nft/types"
import type { CartPaymentSupport } from "@rarible_int/anaconda_sdk/ethereum"
import type { PurchasableNft } from "./scope"

export function isNftAvailableToBuy(
  nft: NftMiniature,
  userWallet: UnionAddress | undefined,
): nft is PurchasableNft {
  if (!nft.price) return false
  if (isExternalListing(nft.listingSource)) return false
  if (userWallet && nft.owner === userWallet) return false

  return true
}

/**
 * Why the cart cannot take this item, or undefined when it can:
 * - `other-currency`: the cart already holds items priced in another currency;
 * - `unsupported-token`: priced in a token the chain's cart contract cannot
 *   pay in (it takes the native currency and at most its own WETH);
 * - `opensea-weth`: an OpenSea WETH listing, which the cart contract cannot
 *   pay until its owner approves WETH to Seaport.
 * With `support` unknown (still loading), only the currency rule applies; the
 * purchase itself checks the rest.
 */
export type CartPaymentRefusal =
  | "other-currency"
  | "unsupported-token"
  | "opensea-weth"

export function getCartPaymentRefusal(
  nft: NftMiniature,
  cart: NftMiniature[],
  support: CartPaymentSupport | undefined,
): CartPaymentRefusal | undefined {
  const currencyId = nft.price?.currency.id
  if (!currencyId) return undefined

  const cartCurrencyId = cart.find(({ id }) => id !== nft.id)?.price?.currency
    .id
  if (cartCurrencyId && !sameId(cartCurrencyId, currencyId)) {
    return "other-currency"
  }
  if (isNativeCurrencyId(currencyId) || !support) return undefined

  const token = currencyId.slice(currencyId.lastIndexOf(":") + 1)
  if (!support.weth || !sameId(token, support.weth)) return "unsupported-token"
  if (nft.bestSellOrder?.platform === "OPEN_SEA" && !support.seaportWeth) {
    return "opensea-weth"
  }
  return undefined
}

/** `POLYGON:0x000…000` is the chain's native currency; anything else is a token. */
function isNativeCurrencyId(currencyId: string): boolean {
  return /^0x0{40}$/i.test(currencyId.slice(currencyId.lastIndexOf(":") + 1))
}

function sameId(a: string, b: string): boolean {
  return a.toLowerCase() === b.toLowerCase()
}
